import { CreateFontPack, FontPack } from '@/types/theme-packs.types';
import AddNewFontSelect from './theme-pack-add-new-font-select';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { useState, useMemo, useEffect } from 'react';
import { GetEmptyFontPack } from './utils/get-empty-font-pack';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import { CreateFont } from '@/connection/font-packs';
import { useGetFontPackOptions } from '@/utils/use-get-font-pack-options';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import { TGlobalContainerDesigns } from '@styles/design-system/global-components/global-components.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { random } from 'underscore';
import { EToastTypes } from '@ui/toast/toast.types';

interface IAddNewFontProps {
  fontPacks?: FontPack[];
  handleFontPackCreated?: (fontPack: FontPack) => void;
  cancelFontPackAdd?: () => void;
}

const AddNewFont = ({ fontPacks, handleFontPackCreated, cancelFontPackAdd }: IAddNewFontProps) => {
  const { headingFonts, bodyFonts } = useGetFontPackOptions();

  const [saving, setSaving] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [selectedHeadingFont, setSelectedHeadingFont] = useState<
    TReactSelectOption<string, string, { url: string; name: string }> | undefined
  >();
  const [selectedBodyFont, setSelectedBodyFont] = useState<
    TReactSelectOption<string, string, { url: string; name: string }> | undefined
  >();

  useEffect(() => {
    setSaveDisabled(!selectedBodyFont || !selectedHeadingFont);
  }, [selectedBodyFont, selectedHeadingFont]);

  const isValidFontPack = useMemo(() => {
    if (!selectedHeadingFont?.value || !selectedBodyFont?.value) {
      return false;
    }

    if (!selectedBodyFont?.meta?.name || !selectedBodyFont.meta?.url) {
      return false;
    }

    if (!selectedHeadingFont?.meta?.name || !selectedHeadingFont.meta?.url) {
      return false;
    }

    return true;
  }, [selectedBodyFont, selectedHeadingFont]);

  const handleSaveFontCombination = async () => {
    if (!isValidFontPack) {
      return;
    }

    let fontPack: FontPack | null = null;

    try {
      setSaving(true);

      const matchedPack = fontPacks?.find((pack) => {
        return pack.heading_url === selectedHeadingFont?.value && pack.body_url === selectedBodyFont?.value;
      });
      if (matchedPack) {
        //if we find a match, return the matched pack
        fontPack = matchedPack;
      } else {
        const matchedHeadingPack = selectedHeadingFont?.meta;
        const matchedBodyPack = selectedBodyFont?.meta;
        if (!matchedHeadingPack || !matchedBodyPack) {
          setSaving(false);
          throw new Error('Could not find font packs for selected fonts');
        }
        const newFontPack: CreateFontPack = {
          ...GetEmptyFontPack(),
          title: `${selectedHeadingFont.label} & ${selectedBodyFont.label}`,
          description: 'Custom font',
          body_url: matchedBodyPack.url,
          body_name: matchedBodyPack.name,
          heading_url: matchedHeadingPack.url,
          heading_name: matchedHeadingPack.name,
        };

        if (!newFontPack.body_name.includes('__custom')) {
          newFontPack.body_name += `__custom-${random(6)}__`;
        }
        if (!newFontPack.heading_name.includes('__custom')) {
          newFontPack.heading_name += `__custom-${random(6)}__`;
        }

        fontPack = await CreateFont(newFontPack);
      }

      if (handleFontPackCreated && fontPack) {
        handleFontPackCreated(fontPack);
      }
    } catch (e) {
      console.error(e);
      toastTrigger('There was a problem creating the font pack', {
        type: EToastTypes.error,
        autoClose: 10000,
      });
    } finally {
      setSelectedHeadingFont(undefined);
      setSelectedBodyFont(undefined);
      setSaving(false);
    }
  };

  const handleCancelFontPackAdd = () => {
    setSelectedHeadingFont(undefined);
    setSelectedBodyFont(undefined);
    cancelFontPackAdd?.();
  };

  return (
    <Container className="flex fd-row padding-16 jc-center ai-center width-100-percent">
      <Container
        className="flex fd-row padding-16 jc-center"
        width="638px"
        height="64px"
        design={TGlobalContainerDesigns.GlobalCont1}
        gap="16px"
        borderRadius="8px"
      >
        <Container className="flex fd-row" gap="16px">
          <AddNewFontSelect
            placeholder="Select Header Font"
            setSelectedFont={setSelectedHeadingFont}
            selectedFont={selectedHeadingFont}
            options={headingFonts}
          />
          <AddNewFontSelect
            placeholder="Select Body Font"
            setSelectedFont={setSelectedBodyFont}
            selectedFont={selectedBodyFont}
            options={bodyFonts}
          />
        </Container>
        <Container className="flex fd-row" gap="6px">
          <Button
            className="L2"
            onClick={handleCancelFontPackAdd}
            disabled={saving}
            design={TButtonAndLinkDesigns.LocalBttnSec}
            height="32px"
          >
            <Text color="var(--local-bttn-sec-default-icotext)">Cancel</Text>
          </Button>
          <Button
            className="L2"
            onClick={handleSaveFontCombination}
            disabled={saveDisabled || saving}
            backgroundColor={saveDisabled ? 'var(--local-bttn-prim-disabled-bg)' : 'var(--local-bttn-prim-default-bg)'}
            hoverBackgroundColor="var(--local-bttn-prim-hover-bg)"
            height="32px"
          >
            <Text
              color={
                saveDisabled ? 'var(--local-bttn-prim-disabled-icotext)' : 'var(--local-bttn-prim-default-icotext)'
              }
            >
              Save
            </Text>
          </Button>
        </Container>
      </Container>
    </Container>
  );
};

export default AddNewFont;
